import type { AgentReferenceType } from '@nest-react/domain';
import type { StateCreator } from 'zustand';

import { listWorkspaceAgentReferences } from '~/repositories/agentReferenceRepository';
import { privateAssetUrl } from '~/repositories/assetRepository';

import type { WorkspaceStore } from '../workspaceStore';
import {
  type WorkspaceAgentReferenceItem,
  type WorkspaceAgentReferencesSnapshot,
  emptyAgentReferencesSnapshot,
} from '../types';

export type AgentReferencesSlice = {
  agentReferencesByWorkspace: Record<string, WorkspaceAgentReferencesSnapshot>;
  fetchWorkspaceAgentReferences: (
    workspaceId: string,
    apiUrl: string
  ) => Promise<void>;
  prependWorkspaceAgentReference: (
    workspaceId: string,
    item: WorkspaceAgentReferenceItem
  ) => void;
  resetWorkspaceAgentReferences: (workspaceId?: string) => void;
};

const patchReferences = (
  current: Record<string, WorkspaceAgentReferencesSnapshot>,
  workspaceId: string,
  patch: Partial<WorkspaceAgentReferencesSnapshot>
): Record<string, WorkspaceAgentReferencesSnapshot> => ({
  ...current,
  [workspaceId]: {
    ...(current[workspaceId] ?? emptyAgentReferencesSnapshot()),
    ...patch,
  },
});

const mapReference = async (
  workspaceId: string,
  reference: Awaited<ReturnType<typeof listWorkspaceAgentReferences>>[number]
): Promise<WorkspaceAgentReferenceItem> => ({
  id: `workspace-reference:${reference.id}`,
  assetId: reference.sourceAssetId,
  previewUrl: await privateAssetUrl(workspaceId, reference.sourceAssetId),
  type: 'IMAGE',
  name: reference.name,
  referenceName: reference.name,
  referenceId: reference.id,
  referenceType: reference.type as AgentReferenceType,
  uploadStatus: 'ready',
});

export const createAgentReferencesSlice: StateCreator<
  WorkspaceStore,
  [],
  [],
  AgentReferencesSlice
> = set => ({
  agentReferencesByWorkspace: {},
  fetchWorkspaceAgentReferences: async (workspaceId, apiUrl) => {
    set(state => ({
      agentReferencesByWorkspace: patchReferences(
        state.agentReferencesByWorkspace,
        workspaceId,
        { loading: true }
      ),
    }));
    try {
      const references = await listWorkspaceAgentReferences(
        apiUrl,
        workspaceId
      );
      const items = await Promise.all(
        references.map(reference => mapReference(workspaceId, reference))
      );
      set(state => ({
        agentReferencesByWorkspace: patchReferences(
          state.agentReferencesByWorkspace,
          workspaceId,
          {
            items,
            loading: false,
            loaded: true,
          }
        ),
      }));
    } catch {
      set(state => ({
        agentReferencesByWorkspace: patchReferences(
          state.agentReferencesByWorkspace,
          workspaceId,
          {
            items: [],
            loading: false,
            loaded: true,
          }
        ),
      }));
    }
  },
  prependWorkspaceAgentReference: (workspaceId, item) => {
    set(state => {
      const current =
        state.agentReferencesByWorkspace[workspaceId] ??
        emptyAgentReferencesSnapshot();
      return {
        agentReferencesByWorkspace: patchReferences(
          state.agentReferencesByWorkspace,
          workspaceId,
          {
            items: [
              item,
              ...current.items.filter(
                candidate => candidate.assetId !== item.assetId
              ),
            ],
          }
        ),
      };
    });
  },
  resetWorkspaceAgentReferences: workspaceId => {
    if (!workspaceId) {
      set({ agentReferencesByWorkspace: {} });
      return;
    }
    set(state => {
      const next = { ...state.agentReferencesByWorkspace };
      delete next[workspaceId];
      return { agentReferencesByWorkspace: next };
    });
  },
});
